// PROTOTYPE — "אוטומציות v2" management page.
// Styled to match the Forms (טפסים) page: coachTokens `t`, title row + table card.
// Three views inside the card: list → executions → run detail. Stub data only.

import {useEffect, useMemo, useState} from "react";
import {
    Box, Chip, Dialog, DialogContent, IconButton, Switch,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import {
    Add as AddIcon, Close as CloseIcon, BoltOutlined as BoltIcon,
    ChevronRight as BackIcon, HistoryOutlined as HistoryIcon,
    CheckCircle as OkIcon, Cancel as FailIcon, RemoveCircleOutline as SkipIcon,
    Search as SearchIcon, DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import {EmailOutlined as EmailIcon, WebhookOutlined as WebhookIcon, ArrowBackIosNew as GoIcon, EditOutlined as EditIcon} from "@mui/icons-material";
import {useNavigate, useSearchParams} from "react-router-dom";
import {t} from "../componenets/dashboard/coach/coachTokens";
import {STATUS_META, INTEGRATIONS, TEMPLATES} from "../componenets/automations/flow/mockAutomations";
import {listFlows, enableFlow, disableFlow, deleteFlow, listExecutions, getExecution} from "../services/AutomationFlowService";
import {useMessageContext} from "../contexts/MessageContext";
import {useCompany} from "../contexts/CompanyContext";
import {useAuth} from "../contexts/AuthContext";
import arboxLogo from "../componenets/../assets/images/arbox.webp";
import fireberryLogo from "../assets/images/fireberry.webp";

// Split the mocked templates into the "blank" start option and the real ready-made templates,
// so the create dialog can present blank as a distinct first choice above the template blocks.
const BLANK_TEMPLATE = TEMPLATES.find(x => x.blank) || {id: "tmpl_blank", blank: true};
const REAL_TEMPLATES = TEMPLATES.filter(x => !x.blank);

// Map a server FlowResponse into the list-row shape the table renders.
const TRIGGER_LABEL = {
    ARBOX_NEW_CLIENT: "Arbox — לקוח חדש", ARBOX_CLIENT_STATUS: "Arbox — סטטוס לקוח",
    ARBOX_CLIENT_DISABLED: "Arbox — לקוח הושבת", ARBOX_CLIENT_ENABLED: "Arbox — לקוח הופעל",
    FIREBERRY_NEW_CONTACT: "Fireberry — איש קשר חדש", FIREBERRY_CONTACT_UPDATED: "Fireberry — עודכן",
    WEBHOOK_RECEIVED: "Webhook נכנס", INTERNAL_EVENT: "אירוע מערכת",
};
const toRow = (f) => ({
    id: f.id,
    name: f.name,
    enabled: f.enabled,
    trigger: TRIGGER_LABEL[f.triggerKind] || f.triggerKind,
    steps: f.stepCount ?? 0,                     // server-computed (list omits the full graph)
    lastRun: f.lastRunAt ? new Date(f.lastRunAt).toLocaleString("he-IL") : "—",
    raw: f,
});

// Integration/template icon renderer.
const IntIcon = ({iconType, size = 26}) => {
    if (iconType === "arbox") return <Box component="img" src={arboxLogo} alt="" sx={{width: size, height: size, borderRadius: "6px"}}/>;
    if (iconType === "fireberry") return <Box component="img" src={fireberryLogo} alt="" sx={{width: size, height: size, borderRadius: "50%"}}/>;
    if (iconType === "webhook") return <Box sx={{width: size, height: size, borderRadius: "6px", background: t.t2, display: "flex", alignItems: "center", justifyContent: "center"}}><WebhookIcon sx={{fontSize: size * 0.66, color: "#fff"}}/></Box>;
    if (iconType === "email") return <Box sx={{width: size, height: size, borderRadius: "6px", background: t.green, display: "flex", alignItems: "center", justifyContent: "center"}}><EmailIcon sx={{fontSize: size * 0.62, color: "#fff"}}/></Box>;
    return null;
};

const td = {padding: "10px 12px", borderBottom: `1px solid ${t.border}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

const StatusPill = ({status}) => {
    const m = STATUS_META[status] || STATUS_META.SKIPPED;
    return <Chip label={m.label} size="small" sx={{height: 22, fontSize: "11px", fontWeight: 700, bgcolor: m.bg, color: m.color}}/>;
};
const StepIcon = ({status}) => {
    if (status === "SUCCESS") return <OkIcon sx={{fontSize: 20, color: STATUS_META.SUCCESS.color}}/>;
    if (status === "FAILED") return <FailIcon sx={{fontSize: 20, color: STATUS_META.FAILED.color}}/>;
    return <SkipIcon sx={{fontSize: 20, color: STATUS_META.SKIPPED.color}}/>;
};

const greenBtn = {
    display: "inline-flex", alignItems: "center", gap: "5px",
    background: t.green, color: "#fff", height: 34, px: "15px", borderRadius: t.rs,
    fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
    border: "none", cursor: "pointer", fontFamily: "inherit", transition: "all .14s",
    "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
};
const backBtn = {
    display: "inline-flex", alignItems: "center", gap: "3px", background: "none", border: "none",
    color: t.t2, fontSize: "12.5px", fontWeight: 600, cursor: "pointer", fontFamily: "inherit",
    "&:hover": {color: t.green},
};

// A key names a date field if its last segment looks time-related (dateOfBirth, lastTrained,
// sentAt, createdDate, expirationDate, ...). Used only to prettify the DISPLAY — the raw value the
// automation actually sends is never changed.
const isDateKey = (key) => {
    const seg = String(key).split(".").pop();
    // Case-INSENSITIVE date words, but the camelCase "...At" suffix (sentAt, createdAt) is matched
    // CASE-SENSITIVELY — otherwise /At/i also matches the "at" inside "locATion", wrongly formatting
    // location_id as a 1970 date.
    return /(date|dob|birth|trained|expiration)/i.test(seg) || /At$/.test(seg);
};

// Format a date-ish value for display: ISO string, epoch-seconds (~10 digits), or epoch-millis
// (~13 digits). Returns null if it can't be parsed as a date (so the caller shows the raw value).
const formatDateValue = (v) => {
    if (v == null || v === "") return null;
    let d;
    if (typeof v === "number" || /^\d+$/.test(String(v))) {
        const n = Number(v);
        d = new Date(String(n).length <= 10 ? n * 1000 : n);   // seconds vs. millis
    } else {
        d = new Date(v);   // ISO string
    }
    return isNaN(d.getTime()) ? null : d.toLocaleString("he-IL");
};

// Render a value: dates get human-formatted; everything else is shown as-is.
const displayValue = (key, v) => {
    if (v === "") return "(ריק)";
    if (isDateKey(key)) {
        const formatted = formatDateValue(v);
        if (formatted) return formatted;
    }
    return String(v);
};

function DataGrid({data}) {
    const entries = Object.entries(data || {});
    if (!entries.length) return <Typography sx={{fontSize: "12px", color: t.t3}}>—</Typography>;
    return (
        <Box sx={{display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px"}}>
            {entries.map(([k, v]) => (
                <Box key={k} sx={{display: "contents"}}>
                    <Typography sx={{fontSize: "12px", fontFamily: "monospace", color: t.t3, direction: "ltr", textAlign: "left"}}>{k}</Typography>
                    <Typography sx={{fontSize: "12px", fontWeight: 600, color: t.text, wordBreak: "break-all"}}>{displayValue(k, v)}</Typography>
                </Box>
            ))}
        </Box>
    );
}

// Flatten the nested triggerSnapshot ({arbox:{email}}) to dotted keys for DataGrid.
const flattenSnapshot = (snap) => {
    const out = {};
    Object.entries(snap || {}).forEach(([ns, obj]) => {
        if (obj && typeof obj === "object") {
            Object.entries(obj).forEach(([k, v]) => { out[`${ns}.${k}`] = v; });
        } else {
            out[ns] = obj;
        }
    });
    return out;
};

function RunDetail({executionId, onBack}) {
    const [execution, setExecution] = useState(null);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        getExecution(executionId)
            .then(res => { if (alive) setExecution(res?.data || null); })
            .catch(() => { if (alive) setExecution(null); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [executionId]);

    if (loading) return <Box sx={{p: 6, textAlign: "center"}}><Typography sx={{fontSize: "14px", color: t.t3}}>טוען…</Typography></Box>;
    if (!execution) return <Box sx={{p: 6, textAlign: "center"}}><Typography sx={{fontSize: "14px", color: t.t3}}>ההרצה לא נמצאה</Typography></Box>;

    const ms = durationMs(execution);
    return (
        <Box sx={{p: "16px 18px"}}>
            <Box component="button" onClick={onBack} sx={{...backBtn, mb: 1.5}}><BackIcon sx={{fontSize: 18}}/>חזרה להרצות</Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 1.5, mb: 2}}>
                <Typography sx={{fontSize: "15px", fontWeight: 700, color: t.text}}>הרצה {execution.id}</Typography>
                <StatusPill status={execution.status}/>
                <Typography sx={{fontSize: "12px", color: t.t3}}>{fmtTime(execution.startedAt)}{ms == null ? "" : ` · ${ms}ms`}</Typography>
            </Box>

            <Box sx={{mb: 2, p: 1.5, border: `1px solid ${t.border}`, borderRadius: t.rs, background: t.greenLight}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 0.5}}>
                    <BoltIcon sx={{fontSize: 16, color: t.green}}/>
                    <Typography sx={{fontSize: "13px", fontWeight: 700, color: t.text}}>נתוני הטריגר</Typography>
                </Box>
                <DataGrid data={flattenSnapshot(execution.triggerSnapshot)}/>
            </Box>

            <Box sx={{display: "flex", flexDirection: "column", gap: 1}}>
                {(execution.steps || []).map((s, i) => (
                    <Box key={i} sx={{border: `1px solid ${t.border}`, borderRadius: t.rs, overflow: "hidden"}}>
                        <Box sx={{display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 1, background: t.bg}}>
                            <StepIcon status={s.status}/>
                            <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text, flex: 1}}>{s.kind}</Typography>
                            <StatusPill status={s.status}/>
                        </Box>
                        {s.status === "FAILED" && s.errorMessage && (
                            <Box sx={{px: 1.5, py: 1, background: t.redLight}}>
                                <Typography sx={{fontSize: "12px", color: t.redText, fontWeight: 600}}>שגיאה: {s.errorMessage}</Typography>
                            </Box>
                        )}
                        {s.status === "SUCCESS" && s.output && (
                            <Box sx={{px: 1.5, py: 1}}>
                                <Typography sx={{fontSize: "11px", color: t.t3, mb: 0.5}}>נתונים שנוצרו:</Typography>
                                <DataGrid data={s.output}/>
                            </Box>
                        )}
                        {s.status === "SKIPPED" && (
                            <Box sx={{px: 1.5, py: 1}}><Typography sx={{fontSize: "12px", color: t.t3}}>דולג — שלב קודם נכשל או תנאי לא התקיים</Typography></Box>
                        )}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// Duration between two ISO timestamps, in ms (or null).
const durationMs = (r) => (r.startedAt && r.finishedAt)
    ? new Date(r.finishedAt).getTime() - new Date(r.startedAt).getTime() : null;
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("he-IL") : "—";

function Executions({automation, onOpenRun, onBack}) {
    const [runs, setRuns] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        let alive = true;
        listExecutions(automation.id)
            .then(res => { if (alive) setRuns(res?.data || []); })
            .catch(() => { if (alive) setRuns([]); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [automation.id]);
    return (
        <Box>
            <Box sx={{p: "12px 18px", borderBottom: `1px solid ${t.border}`, background: t.bg}}>
                <Box component="button" onClick={onBack} sx={backBtn}><BackIcon sx={{fontSize: 18}}/>חזרה לרשימה</Box>
                <Typography sx={{fontSize: "15px", fontWeight: 700, color: t.text, mt: 0.5}}>{automation.name}</Typography>
            </Box>
            {loading ? (
                <Box sx={{p: 6, textAlign: "center"}}><Typography sx={{fontSize: "14px", color: t.t3}}>טוען…</Typography></Box>
            ) : runs.length === 0 ? (
                <Box sx={{p: 6, textAlign: "center"}}>
                    <HistoryIcon sx={{fontSize: 44, color: t.border}}/>
                    <Typography sx={{fontSize: "14px", color: t.t3, mt: 1}}>אין הרצות עדיין</Typography>
                </Box>
            ) : (
                <TableContainer sx={{direction: "rtl"}}>
                    <Table>
                        <TableHead><TableRow>
                            <TableCell sx={{...th, textAlign: "right"}}>הרצה</TableCell>
                            <TableCell sx={{...th, textAlign: "right"}}>סטטוס</TableCell>
                            <TableCell sx={{...th, textAlign: "right"}}>זמן</TableCell>
                            <TableCell sx={{...th, textAlign: "right"}}>משך</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                            {runs.map(r => {
                                const ms = durationMs(r);
                                return (
                                    <TableRow key={r.id} sx={{cursor: "pointer", "&:hover td": {background: t.greenLight}}} onClick={() => onOpenRun(r.id)}>
                                        <TableCell sx={{...td, textAlign: "right", fontFamily: "monospace"}}>{r.id}</TableCell>
                                        <TableCell sx={{...td, textAlign: "right"}}><StatusPill status={r.status}/></TableCell>
                                        <TableCell sx={{...td, textAlign: "right"}}>{fmtTime(r.startedAt)}</TableCell>
                                        <TableCell sx={{...td, textAlign: "right"}}>{ms == null ? "—" : `${ms}ms`}</TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
}

// Integrations "what's possible" strip.
function IntegrationsStrip() {
    return (
        <Box sx={{display: "flex", gap: 1.5, flexWrap: "wrap"}}>
            {INTEGRATIONS.map(i => (
                <Box key={i.id} sx={{
                    flex: "1 1 200px", minWidth: 190, display: "flex", alignItems: "center", gap: 1.25,
                    p: 1.25, border: `1px solid ${t.border}`, borderRadius: t.rs, background: t.white,
                }}>
                    <IntIcon iconType={i.iconType}/>
                    <Box>
                        <Typography sx={{fontSize: "13px", fontWeight: 700, color: t.text}}>{i.label}</Typography>
                        <Typography sx={{fontSize: "11px", color: t.t3}}>{i.blurb}</Typography>
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

// A single template recipe card.
function TemplateCard({tmpl, onUse}) {
    const accentIcon = {arbox: "arbox", fireberry: "fireberry", webhook: "webhook"}[tmpl.accent];
    return (
        <Box onClick={() => onUse(tmpl)} sx={{
            cursor: "pointer", p: 1.75, border: `1px solid ${t.border}`, borderRadius: t.r, background: t.white,
            height: "100%", boxSizing: "border-box",
            display: "flex", flexDirection: "column", gap: 1, transition: "all .14s",
            "&:hover": {borderColor: t.green, boxShadow: t.sh, transform: "translateY(-2px)"},
        }}>
            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                {tmpl.blank
                    ? <Box sx={{width: 26, height: 26, borderRadius: "6px", background: t.greenLight, display: "flex", alignItems: "center", justifyContent: "center"}}><AddIcon sx={{fontSize: 18, color: t.green}}/></Box>
                    : accentIcon ? <IntIcon iconType={accentIcon}/> : <Box sx={{width: 26, height: 26, borderRadius: "6px", background: t.green, display: "flex", alignItems: "center", justifyContent: "center"}}><BoltIcon sx={{fontSize: 16, color: "#fff"}}/></Box>}
                {tmpl.badge && <Chip label={tmpl.badge} size="small" sx={{height: 20, fontSize: "10px", fontWeight: 700, bgcolor: t.greenLight, color: t.greenText}}/>}
                <GoIcon sx={{fontSize: 13, color: t.t3, mr: "auto"}}/>
            </Box>
            <Typography sx={{fontSize: "13.5px", fontWeight: 700, color: t.text, lineHeight: 1.3}}>{tmpl.title}</Typography>
            <Typography sx={{fontSize: "11.5px", color: t.t2, lineHeight: 1.4, flex: 1}}>{tmpl.blurb}</Typography>
            {tmpl.steps.length > 0 && (
                <Box sx={{display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", mt: "auto", pt: 0.5}}>
                    {tmpl.steps.map((s, i) => (
                        <Box key={i} sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                            <Box sx={{px: 0.75, py: 0.25, borderRadius: "6px", background: t.bg, fontSize: "10px", color: t.t2, fontWeight: 600, whiteSpace: "nowrap"}}>{s}</Box>
                            {i < tmpl.steps.length - 1 && <Typography sx={{fontSize: "11px", color: t.t3}}>←</Typography>}
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
}

export default function AutomationV2Page() {
    const navigate = useNavigate();
    const {notificationHandler} = useMessageContext();
    const {company} = useCompany();
    const {isSuperAdmin} = useAuth();
    // SYSTEM_ADMIN must scope the list to the selected company; regular admins are pinned server-side.
    const companyId = isSuperAdmin() ? company?.id : undefined;
    const needsCompany = isSuperAdmin() && !company?.id;
    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(true);
    // View is driven by the URL so a refresh restores the same place:
    //   ?flow=<id>          → that automation's executions
    //   ?flow=<id>&exec=<id> → a specific run's detail
    const [searchParams, setSearchParams] = useSearchParams();
    const flowId = searchParams.get("flow");
    const openRunId = searchParams.get("exec");
    // Resolve the selected row from the loaded list (so name/steps are available after a refresh).
    const selected = flowId ? (automations.find(a => String(a.id) === String(flowId)) || {id: flowId, name: ""}) : null;

    const openExecutions = (row) => setSearchParams({flow: String(row.id)});
    const openRun = (execId) => setSearchParams({flow: String(flowId), exec: String(execId)});
    const backToList = () => setSearchParams({});
    const backToExecutions = () => setSearchParams({flow: String(flowId)});

    const [keyword, setKeyword] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [newName, setNewName] = useState("");
    const [newEnabled, setNewEnabled] = useState(true);
    const [chosenTemplate, setChosenTemplate] = useState(null);

    const reload = () => {
        // A super-admin with "All Companies" selected has no company to scope to — show the
        // prompt instead of firing a request the server would reject.
        if (needsCompany) {
            setAutomations([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        listFlows(companyId)
            .then(res => setAutomations((res?.data || []).map(toRow)))
            .catch(() => setAutomations([]))
            .finally(() => setLoading(false));
    };
    // Reload when the selected company changes (super-admin switching companies in the top bar).
    useEffect(reload, [companyId, needsCompany]);

    const toggle = async (row) => {
        // optimistic flip, revert on failure
        setAutomations(a => a.map(x => x.id === row.id ? {...x, enabled: !x.enabled} : x));
        try {
            await (row.enabled ? disableFlow(row.id) : enableFlow(row.id));
        } catch (e) {
            setAutomations(a => a.map(x => x.id === row.id ? {...x, enabled: row.enabled} : x));
            notificationHandler?.error?.("עדכון הסטטוס נכשל");
        }
    };
    const remove = async (row) => {
        if (!window.confirm(`למחוק את "${row.name}"?`)) return;
        try {
            await deleteFlow(row.id);
            setAutomations(a => a.filter(x => x.id !== row.id));
        } catch (e) {
            notificationHandler?.error?.("מחיקה נכשלה");
        }
    };
    const openCreate = () => { setNewName(""); setChosenTemplate(null); setNewEnabled(true); setCreateOpen(true); };
    const createAutomation = () => {
        setCreateOpen(false); setNewName("");
        // Seed the builder from the chosen template (blank → no seed).
        const q = chosenTemplate && !chosenTemplate.blank ? `?template=${chosenTemplate.id}` : "";
        navigate(`/app/automations-flow${q}`);
    };
    // Using a template card from the page jumps straight into the builder, seeded.
    const useTemplate = (tmpl) => {
        const q = tmpl && !tmpl.blank ? `?template=${tmpl.id}` : "";
        navigate(`/app/automations-flow${q}`);
    };
    // Edit an existing automation → builder in edit mode (?id=… loads + saves the flow).
    const editAutomation = (a) => navigate(`/app/automations-flow?id=${a.id}`);

    const filtered = useMemo(
        () => automations.filter(a => a.name.toLowerCase().includes(keyword.toLowerCase())),
        [automations, keyword]
    );
    const view = openRunId ? "run" : selected ? "executions" : "list";
    const isEmpty = !loading && !needsCompany && automations.length === 0;

    return (
        <Box sx={{display: "flex", flexDirection: "column", gap: "18px", direction: "rtl"}}>
            {/* Title row */}
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
                <Box>
                    <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
                        אוטומציות v2
                    </Typography>
                    <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול אוטומציות והרצות</Typography>
                </Box>
                {view === "list" && !needsCompany && (
                    <Box component="button" onClick={openCreate} sx={greenBtn}>
                        <AddIcon sx={{fontSize: 16}}/>אוטומציה חדשה
                    </Box>
                )}
            </Box>

            {/* Super-admin must pick a company (the automations are company-scoped). */}
            {needsCompany && (
                <Box sx={{p: 5, textAlign: "center", background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r}}>
                    <BoltIcon sx={{fontSize: 44, color: t.border}}/>
                    <Typography sx={{fontSize: "15px", fontWeight: 700, color: t.text, mt: 1}}>בחר חברה</Typography>
                    <Typography sx={{fontSize: "13px", color: t.t3, mt: 0.5}}>
                        אוטומציות משויכות לחברה. בחר חברה בסרגל העליון כדי לנהל את האוטומציות שלה.
                    </Typography>
                </Box>
            )}

            {/* Empty state — no automations yet: lead with integrations + full template gallery */}
            {view === "list" && isEmpty && (
                <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>
                    <Box>
                        <Typography sx={{fontSize: "15px", fontWeight: 800, color: t.text, mb: 1}}>מה אפשר לחבר</Typography>
                        <IntegrationsStrip/>
                    </Box>
                    <Box>
                        <Typography sx={{fontSize: "15px", fontWeight: 800, color: t.text, mb: 1}}>התחל מתבנית מוכנה</Typography>
                        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr"}, gap: "14px", p: "4px", pt: "6px"}}>
                            {TEMPLATES.map(tmpl => <TemplateCard key={tmpl.id} tmpl={tmpl} onUse={useTemplate}/>)}
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Card — only when there are automations to list (or in sub-views) */}
            {(!isEmpty || view !== "list") && (
            <Box sx={{background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r, boxShadow: t.sh, overflow: "hidden", display: "flex", flexDirection: "column"}}>
                {view === "list" && (
                    <>
                        {/* Filter bar */}
                        <Box sx={{display: "flex", alignItems: "center", gap: "7px", padding: "10px 14px",
                                  borderBottom: `1px solid ${t.border}`, background: t.bg, flexWrap: "wrap"}}>
                            <Box sx={{display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 700,
                                      color: t.greenText, background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
                                      padding: "4px 12px", borderRadius: "20px"}}>
                                <BoltIcon sx={{fontSize: 14}}/>{automations.length} אוטומציות
                            </Box>
                            <Box sx={{position: "relative"}}>
                                <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
                                <TextField value={keyword} onChange={e => setKeyword(e.target.value)} placeholder="חיפוש לפי שם..."
                                    variant="standard" InputProps={{disableUnderline: true}}
                                    sx={{width: 220, "& input": {padding: "6px 10px 6px 30px", fontSize: "12px"},
                                         border: `1.5px solid ${t.border}`, borderRadius: t.rs, background: t.white,
                                         "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"}}}/>
                            </Box>
                        </Box>

                        <TableContainer sx={{direction: "rtl"}}>
                            <Table>
                                <TableHead><TableRow>
                                    <TableCell sx={{...th, textAlign: "right"}}>שם</TableCell>
                                    <TableCell sx={{...th, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>טריגר</TableCell>
                                    <TableCell sx={{...th, textAlign: "center", width: 80}}>שלבים</TableCell>
                                    <TableCell sx={{...th, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>הרצה אחרונה</TableCell>
                                    <TableCell sx={{...th, textAlign: "center", width: 90}}>פעיל</TableCell>
                                    <TableCell sx={{...th, textAlign: "center", width: 110}}>פעולות</TableCell>
                                </TableRow></TableHead>
                                <TableBody>
                                    {filtered.map(a => (
                                        <TableRow key={a.id} sx={{cursor: "pointer", "&:hover td": {background: t.greenLight}}} onClick={() => openExecutions(a)}>
                                            <TableCell sx={{...td, textAlign: "right", fontWeight: 600}}>{a.name}</TableCell>
                                            <TableCell sx={{...td, textAlign: "right", color: t.t2, display: {xs: "none", md: "table-cell"}}}>{a.trigger}</TableCell>
                                            <TableCell sx={{...td, textAlign: "center"}}>{a.steps}</TableCell>
                                            <TableCell sx={{...td, textAlign: "center", color: t.t3, display: {xs: "none", md: "table-cell"}}}>{a.lastRun}</TableCell>
                                            <TableCell sx={{...td, textAlign: "center"}} onClick={e => e.stopPropagation()}>
                                                <Switch size="small" checked={a.enabled} onChange={() => toggle(a)}/>
                                            </TableCell>
                                            <TableCell sx={{...td, textAlign: "center"}} onClick={e => e.stopPropagation()}>
                                                <Box sx={{display: "inline-flex", gap: "2px"}}>
                                                    <Tooltip title="עריכה">
                                                        <IconButton size="small" onClick={() => editAutomation(a)}
                                                                    sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                                                            <EditIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="הרצות">
                                                        <IconButton size="small" onClick={() => openExecutions(a)}
                                                                    sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: t.greenLight}}}>
                                                            <HistoryIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="מחיקה">
                                                        <IconButton size="small" onClick={() => remove(a)}
                                                                    sx={{color: t.t2, "&:hover": {color: t.redText, bgcolor: t.redLight}}}>
                                                            <DeleteIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!filtered.length && (
                                        <TableRow><TableCell colSpan={6} sx={{...td, textAlign: "center", color: t.t3, py: 5}}>לא נמצאו אוטומציות</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}
                {view === "executions" && <Executions automation={selected} onOpenRun={openRun} onBack={backToList}/>}
                {view === "run" && <RunDetail executionId={openRunId} onBack={backToExecutions}/>}
            </Box>
            )}

            {/* Template gallery — below the automations list, like a gallery */}
            {view === "list" && !isEmpty && (
                <Box>
                    <Typography sx={{fontSize: "15px", fontWeight: 800, color: t.text, mb: 1, mt: 1}}>התחל מתבנית</Typography>
                    <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr"}, gap: "14px", p: "4px", pt: "6px"}}>
                        {TEMPLATES.map(tmpl => <TemplateCard key={tmpl.id} tmpl={tmpl} onUse={useTemplate}/>)}
                    </Box>
                </Box>
            )}

            {/* Create dialog — pick a template or blank, name it, toggle enabled */}
            <Dialog open={createOpen} onClose={() => setCreateOpen(false)} PaperProps={{sx: {borderRadius: t.r, maxWidth: 620, direction: "rtl"}}} fullWidth>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 1.5, borderBottom: `1px solid ${t.border}`, background: t.bg}}>
                    <Typography sx={{fontSize: "16px", fontWeight: 700, color: t.text}}>אוטומציה חדשה</Typography>
                    <IconButton size="small" onClick={() => setCreateOpen(false)}><CloseIcon fontSize="small"/></IconButton>
                </Box>
                <DialogContent sx={{p: 2.5, display: "flex", flexDirection: "column", gap: 2}}>
                    {/* 1 — name (top) */}
                    <Box>
                        <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.t2, mb: 0.5}}>שם האוטומציה</Typography>
                        <TextField autoFocus fullWidth size="small" placeholder="לדוגמה: לקוח חדש מ-Arbox"
                                   value={newName} onChange={e => setNewName(e.target.value)}/>
                    </Box>

                    {/* 2 — how to start: blank, or one of the templates (pick exactly one) */}
                    <Box>
                        <Typography sx={{fontSize: "13px", fontWeight: 700, color: t.t2, mb: 1}}>איך להתחיל?</Typography>

                        {/* Blank — a distinct first-class option */}
                        {(() => {
                            const active = chosenTemplate?.blank === true;
                            return (
                                <Box onClick={() => setChosenTemplate(BLANK_TEMPLATE)} sx={{
                                    cursor: "pointer", p: 1.25, mb: 1.5, borderRadius: t.rs,
                                    display: "flex", alignItems: "center", gap: 1.25,
                                    border: `1.5px solid ${active ? t.green : t.border}`,
                                    background: active ? t.greenLight : t.white, transition: "all .12s",
                                    "&:hover": {borderColor: t.green},
                                }}>
                                    <Box sx={{width: 32, height: 32, borderRadius: "8px", flexShrink: 0, background: t.greenLight,
                                              display: "flex", alignItems: "center", justifyContent: "center"}}>
                                        <AddIcon sx={{fontSize: 20, color: t.green}}/>
                                    </Box>
                                    <Box sx={{flex: 1}}>
                                        <Typography sx={{fontSize: "13px", fontWeight: 700, color: t.text}}>התחל מאפס</Typography>
                                        <Typography sx={{fontSize: "11px", color: t.t3}}>בנה אוטומציה ריקה בעצמך</Typography>
                                    </Box>
                                    {active && <OkIcon sx={{fontSize: 18, color: t.green}}/>}
                                </Box>
                            );
                        })()}

                        {/* Or pick a ready-made template */}
                        <Typography sx={{fontSize: "12px", fontWeight: 600, color: t.t3, mb: 0.75}}>או בחר תבנית מוכנה</Typography>
                        <Box sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr"}, gap: "10px", maxHeight: 240, overflowY: "auto"}}>
                            {REAL_TEMPLATES.map(tmpl => {
                                const active = chosenTemplate?.id === tmpl.id;
                                const accentIcon = {arbox: "arbox", fireberry: "fireberry", webhook: "webhook"}[tmpl.accent];
                                return (
                                    <Box key={tmpl.id} onClick={() => {
                                        setChosenTemplate(tmpl);
                                        if (!newName.trim()) setNewName(tmpl.title);
                                    }} sx={{
                                        cursor: "pointer", p: 1.25, borderRadius: t.rs,
                                        border: `1.5px solid ${active ? t.green : t.border}`,
                                        background: active ? t.greenLight : t.white, transition: "all .12s",
                                        "&:hover": {borderColor: t.green},
                                    }}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 0.75, mb: 0.5}}>
                                            {accentIcon ? <IntIcon iconType={accentIcon} size={20}/> : <BoltIcon sx={{fontSize: 16, color: t.green}}/>}
                                            <Typography sx={{fontSize: "12.5px", fontWeight: 700, color: t.text, lineHeight: 1.25}}>{tmpl.title}</Typography>
                                        </Box>
                                        <Typography sx={{fontSize: "11px", color: t.t3, lineHeight: 1.35}}>{tmpl.blurb}</Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>

                    <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
                        <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.t2}}>פעיל</Typography>
                        <Switch checked={newEnabled} onChange={e => setNewEnabled(e.target.checked)}/>
                    </Box>
                    <Box component="button" onClick={createAutomation} disabled={!newName.trim() || !chosenTemplate}
                         sx={{...greenBtn, justifyContent: "center", height: 40,
                              opacity: (newName.trim() && chosenTemplate) ? 1 : 0.5,
                              cursor: (newName.trim() && chosenTemplate) ? "pointer" : "not-allowed"}}>
                        צור אוטומציה
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}
